import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const Home = ({ user }) => {
  const navigate = useNavigate();

  const [liveMods, setLiveMods] = useState(null);
  const [liveRol, setLiveRol] = useState(null);

  const effectiveUser = user || (() => {
    try {
      const saved = sessionStorage.getItem('tabUserData') || localStorage.getItem('userData');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  })();

  const empresaActiva = user?.empresa_activa || effectiveUser?.empresa_activa;
  const currentCompanyId = empresaActiva?.c_nuges || empresaActiva?.id;

  useEffect(() => {
    const code = effectiveUser?.c_usuar || effectiveUser?.nombre;
    if (!code) return;
    axios
      .get('/api/roles-perfiles/', {
        params: { company: currentCompanyId },
        headers: { 'X-Company-ID': String(currentCompanyId) }
      })
      .then((res) => {
        const ops = res.data?.operadores || [];
        const me = ops.find(
          (o) =>
            String(o.c_usuar).trim().toUpperCase() === String(code).trim().toUpperCase() ||
            String(o.d_nomus).trim().toUpperCase() === String(code).trim().toUpperCase()
        );
        if (me && Array.isArray(me.modulos_permitidos)) {
          setLiveMods(me.modulos_permitidos);
          setLiveRol(me.rol);
          try {
            const updateRaw = (raw) => {
              if (!raw) return null;
              const p = JSON.parse(raw);
              return JSON.stringify({
                ...p,
                rol: me.rol,
                rol_id: me.rol_id,
                modulos_permitidos: me.modulos_permitidos,
                permisos_especiales: me.permisos_efectivos,
                l_enwhat: Boolean(me.permisos_efectivos?.l_enwhat),
              });
            };
            const s = updateRaw(sessionStorage.getItem('tabUserData'));
            if (s) sessionStorage.setItem('tabUserData', s);
            const l = updateRaw(localStorage.getItem('userData'));
            if (l) localStorage.setItem('userData', l);
          } catch {}
        }
      })
      .catch(() => {});
  }, [effectiveUser?.c_usuar, effectiveUser?.nombre, currentCompanyId]);

  const userRol = liveRol || effectiveUser?.rol || (effectiveUser?.l_admin ? 'Administrador' : 'Vendedor');
  const sectorStr = String(effectiveUser?.sector || '').trim().toLowerCase();
  const isSupervisorOrAdmin = Boolean(
    effectiveUser?.l_admin ||
    userRol === 'Admin' ||
    userRol === 'Administrador' ||
    userRol === 'Supervisor' ||
    userRol === 'Supervisores' ||
    sectorStr === 'admin' ||
    sectorStr === 'administrador' ||
    sectorStr.includes('superv') ||
    sectorStr.includes('gerenc') ||
    sectorStr.includes('sistemas')
  );

  const activeModList = liveMods || effectiveUser?.modulos_permitidos;

  const canAccessModule = (moduleKey, fallbackAllowed = true) => {
    if (Array.isArray(activeModList) && activeModList.length > 0) {
      return activeModList.includes(moduleKey);
    }
    return fallbackAllowed;
  };

  const [companiesList, setCompaniesList] = useState(() => {
    return Array.isArray(user?.empresas) && user.empresas.length > 0
      ? user.empresas
      : (Array.isArray(effectiveUser?.empresas) ? effectiveUser.empresas : []);
  });

  useEffect(() => {
    axios.get('/api/empresas/').then(res => {
      if (Array.isArray(res.data) && res.data.length > 0) {
        setCompaniesList(res.data);
      }
    }).catch(() => {});
  }, [currentCompanyId]);

  const parseBool = (val) => {
    if (Array.isArray(val)) {
      if (val.length === 0) return false;
      val = val[0];
    }
    if (val === true || val === 1 || val === -1 || val === '1' || val === '-1' || val === 'true' || val === 't' || val === 'v' || val === 'verdadero' || val === 'si' || val === 'sí') return true;
    return false;
  };

  const activeCompanyObj = companiesList.find(e => String(e.c_nuges ?? e.id) === String(currentCompanyId)) || empresaActiva;
  const empresaWebEnabled = parseBool(activeCompanyObj?.l_sitien);

  useEffect(() => {
    setLiveMods(null);
  }, [currentCompanyId]);

  const modulos = [
    {
      id: 2,
      key: 'quoter',
      titulo: 'Cotizador',
      desc: 'Creación y cálculo de cotizaciones para clientes.',
      icon: 'bi-calculator',
      ruta: '/quoter',
      color: 'primary',
      isRestricted: false,
      allowed: canAccessModule('quoter', true),
    },
    {
      id: 3,
      key: 'quote-viewer',
      titulo: 'Consulta de Presupuestos',
      desc: 'Visualizador e historial de comprobantes emitidos.',
      icon: 'bi-file-earmark-text',
      ruta: '/quote-viewer',
      color: 'info',
      isRestricted: false,
      allowed: canAccessModule('quote-viewer', true),
    },
    {
      id: 1,
      key: 'addressbook',
      titulo: 'Agenda de Contactos',
      desc: 'Agenda de contactos, teléfonos y correos de clientes.',
      icon: 'bi-journal-bookmark-fill',
      ruta: '/addressbook',
      color: 'secondary',
      isRestricted: false,
      allowed: canAccessModule('addressbook', true),
    },
    {
      id: 4,
      key: 'imagenes-articulos',
      titulo: 'Imágenes de Artículos',
      desc: 'Galería por artículo, carga de fotos, portada y bajas.',
      icon: 'bi-images',
      ruta: '/imagenes-articulos',
      color: 'warning',
      isRestricted: true,
      allowed: canAccessModule('imagenes-articulos', isSupervisorOrAdmin),
    },
    {
      id: 5,
      key: 'clientes-tienda',
      titulo: 'Clientes Tienda Online',
      desc: 'Usuarios de la tienda, listas de precios y contacto.',
      icon: 'bi-people-fill',
      ruta: '/clientes-tienda',
      color: 'primary',
      isRestricted: true,
      allowed: empresaWebEnabled && canAccessModule('clientes-tienda', isSupervisorOrAdmin),
    },
    {
      id: 6,
      key: 'configuracion-web',
      titulo: 'Configuración Web Unificada',
      desc: 'Portada Web, Tienda Online, banners, colores y contacto.',
      icon: 'bi-globe2',
      ruta: '/configuracion-web',
      color: 'success',
      isRestricted: true,
      allowed: empresaWebEnabled && canAccessModule('configuracion-web', isSupervisorOrAdmin),
    },
    {
      id: 8,
      key: 'roles-perfiles',
      titulo: 'Roles y Perfiles',
      desc: 'Perfiles del sistema, permisos por módulo y operadores.',
      icon: 'bi-shield-lock-fill',
      ruta: '/roles-perfiles',
      color: 'danger',
      isRestricted: true,
      allowed: canAccessModule('roles-perfiles', isSupervisorOrAdmin),
    },
    {
      id: 7,
      key: 'home-web',
      titulo: 'Ver Portada Web (Home)',
      desc: 'Página institucional pública y catálogo online.',
      icon: 'bi-browser-chrome',
      ruta: currentCompanyId ? `/home-web?company=${currentCompanyId}` : '/home-web',
      color: 'info',
      isRestricted: false,
      allowed: empresaWebEnabled,
    },
  ];

  return (
    <>
      {/* Encabezado compacto */}
      <header className="py-3 bg-white border-bottom mb-3">
        <div className="container">
          <div className="text-center py-2">
            <h3 className="fw-bold mb-1">Panel de Control</h3>
            <p className="text-muted small mb-0">
              Seleccioná un módulo para comenzar a trabajar
            </p>
          </div>
        </div>
      </header>

      {/* Grid de Módulos Compacto */}
      <div className="container pb-4">
        <div className="row g-3">
          {modulos.map((mod) => {
            if (!mod.allowed) return null;

            return (
              <div key={mod.id} className="col-12 col-sm-6 col-md-4 col-xl-3">
                <div
                  className={`card h-100 shadow-sm border-0 border-top border-3 border-${mod.color}`}
                  style={{
                    cursor: 'pointer',
                    transition: 'transform 0.16s ease, box-shadow 0.16s ease',
                    borderRadius: '12px',
                  }}
                  onClick={() => navigate(mod.ruta)}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'translateY(-3px)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <div className="card-body p-3 d-flex flex-column justify-content-between">
                    <div>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div
                          className={`rounded-3 d-flex align-items-center justify-content-center bg-${mod.color} bg-opacity-10 text-${mod.color}`}
                          style={{ width: '42px', height: '42px' }}
                        >
                          {mod.icon.startsWith('bi-') ? (
                            <i className={`bi ${mod.icon} fs-5`}></i>
                          ) : (
                            <span className="fs-5">{mod.icon}</span>
                          )}
                        </div>
                        <span
                          className={`badge bg-${mod.color}-subtle text-${mod.color} border border-${mod.color}`}
                          style={{ fontSize: '0.68rem' }}
                        >
                          {mod.isRestricted ? 'Admin / Perfil' : 'Habilitado'}
                        </span>
                      </div>
                      <h6 className="card-title fw-bold mb-1 text-dark">{mod.titulo}</h6>
                      <p
                        className="card-text text-muted mb-0"
                        style={{ fontSize: '0.82rem', lineHeight: '1.35' }}
                      >
                        {mod.desc}
                      </p>
                    </div>
                    <div className="mt-3 pt-2 border-top d-flex align-items-center justify-content-between">
                      <span
                        className={` fw-semibold text-${mod.color}`}
                        style={{ fontSize: '0.78rem' }}
                      >
                        Ingresar
                      </span>
                      <i className={`bi bi-arrow-right-short fs-5 text-${mod.color}`}></i>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default Home;
